import React, { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DynamicsChartFrame } from './DynamicsChartFrame';
import { DYNAMICS_CARD_INSIGHTS } from './dynamicsInsights';
import { DYNAMICS_RECHARTS_TOOLTIP_COMMON } from './dynamicsRechartsTooltip';
import { useDynamicsPlotHeight } from './DynamicsChartLayoutContext';
import { ResolvedPeriod } from '../../PeriodPicker';
import { dynamicsBucketChartMargins, dynamicsPeriodAxisProps } from './dynamicsPeriodAxis';

interface Props {
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  onDataStatus?: (s: { metric: string; hasData: boolean; loading: boolean }) => void;
}

const AREA_COLORS = [
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#0ea5e9',
  '#a855f7',
  '#14b8a6',
  '#ec4899',
];

export const DynamicsK_EscapeTaxonomy: React.FC<Props> = ({
  groupBy,
  lastN,
  resolvedPeriod,
  onDataStatus,
}) => {
  return (
    <DynamicsChartFrame
      id="K"
      title="K · Escape taxonomy"
      hint="Stacked share of escape root causes per period (regression / missed req / env / data / flaky). Growing regression share points at test coverage; growing 'missed req' at PM/design."
      needs="Escape root-cause custom field configured in Quality settings + bugs tagged with a cause."
      metric="escape-taxonomy"
      groupBy={groupBy}
      lastN={lastN}
      resolvedPeriod={resolvedPeriod}
      onDataStatus={onDataStatus}
      chartHeight={320}
      insightsTitle={DYNAMICS_CARD_INSIGHTS.K.title}
      insights={[...DYNAMICS_CARD_INSIGHTS.K.bullets]}
    >
      {(points) => <Inner points={points as Array<Record<string, number | string | null>>} />}
    </DynamicsChartFrame>
  );
};

const Inner: React.FC<{ points: Array<Record<string, number | string | null>> }> = ({ points }) => {
  const plotH = useDynamicsPlotHeight(320);
  const causes = useMemo(() => {
    const set = new Set<string>();
    for (const p of points) {
      for (const k of Object.keys(p)) {
        if (k !== 'period' && typeof p[k] === 'number') set.add(k);
      }
    }
    return Array.from(set);
  }, [points]);

  return (
    <ResponsiveContainer width="100%" height={plotH}>
      <AreaChart data={points} margin={{ ...dynamicsBucketChartMargins }}>
        <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
        <XAxis {...dynamicsPeriodAxisProps} />
        <YAxis />
        <Tooltip {...DYNAMICS_RECHARTS_TOOLTIP_COMMON} />
        <Legend />
        {causes.map((cause, i) => (
          <Area
            key={cause}
            type="monotone"
            dataKey={cause}
            stackId="1"
            stroke={AREA_COLORS[i % AREA_COLORS.length]}
            fill={AREA_COLORS[i % AREA_COLORS.length]}
            name={cause}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};
